import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEvent,
  KeyboardEvent,
  InputHTMLAttributes,
  ReactElement,
  cloneElement,
  forwardRef,
  isValidElement,
  useRef,
  useMemo,
  MouseEvent,
} from "react";
import { TextField, TextFieldProps } from "../TextField";
import { useMergeRefs } from "@floating-ui/react";
import { useOptionallyControlledState } from "../../hooks";

const MASK_CHARS = ["a", "9", "*"];

/**
 * Props for InputMask when using with children
 */
interface InputMaskPropsWithChildren {
  /**
   * Custom input element to be wrapped with mask functionality.
   */
  children: ReactElement<InputHTMLAttributes<HTMLInputElement>>;
  /**
   * Additional props to pass to the input element.
   */
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

/**
 * Props for InputMask when using without children
 */
interface InputMaskPropsWithoutChildren {
  /**
   * No children allowed when using default TextField.
   */
  children?: never;
  /**
   * Props to pass to the default TextField component.
   */
  inputProps?: TextFieldProps;
}

/**
 * Configuration for custom mask handler matching
 */
interface ICustomMaskHandlerMatch {
  /**
   * Regular expression pattern to match against input.
   */
  matchPattern: RegExp;
  /**
   * Starting position in the input string.
   */
  startPosition: number;
  /**
   * Ending position in the input string.
   */
  endPosition: number;
}

/**
 * Configuration for custom mask handler application
 */
interface ICustomMaskHandlerApply {
  /**
   * Pattern to apply when match is found.
   */
  applyPattern: string;
  /**
   * Offset to apply to cursor position after transformation.
   */
  caretOffset: number;
}

/**
 * Custom mask handler for advanced input transformations
 */
interface ICustomMaskHandler {
  /**
   * Matching configuration for the handler.
   */
  match: ICustomMaskHandlerMatch;
  /**
   * Application configuration for the handler.
   */
  apply: ICustomMaskHandlerApply;
}

/**
 * Shared props for InputMask component
 */
interface SharedInputMaskProps {
  /**
   * The mask pattern to apply to the input.
   * - "9" for digits only
   * - "a" for letters only
   * - "*" for alphanumeric
   * - Any other character is treated as a fixed character
   */
  mask: string;
  /**
   * Character to display for unfilled mask positions.
   * @default "_"
   */
  defaultMaskCharacter?: string;
  /**
   * Controlled value for the input.
   */
  value?: string;
  /**
   * Default value for uncontrolled input.
   */
  defaultValue?: string;
  /**
   * Callback function called when input value changes.
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * Array of custom mask handlers for advanced transformations.
   */
  customMaskHandler?: ICustomMaskHandler[];
}

type SpecialChange = {
  reason: "Backspace" | "Delete" | "Edit";
  start: number;
  end: number;
};

/**
 * Props for the InputMask component
 */
export type InputMaskProps = SharedInputMaskProps &
  (InputMaskPropsWithChildren | InputMaskPropsWithoutChildren);

/**
 * InputMask component for applying input masks and formatting to text inputs.
 *
 * Features:
 * - Input masking with configurable patterns (9, a, *, fixed characters)
 * - Support for both controlled and uncontrolled inputs
 * - Custom input elements or default TextField integration
 * - Advanced cursor positioning and selection management
 * - Custom mask handlers for complex transformations
 * - Paste and keyboard event handling
 * - Automatic mask character replacement
 * - Focus and blur event management
 * - Accessible with proper ARIA attributes
 * - Support for all standard input element props
 * - Flexible mask pattern configuration
 * - Real-time input validation and formatting
 *
 * @example
 * <InputMask
 *   mask="99/99/9999"
 *   inputProps={{ label: "Date", hint: "mm/dd/yyyy" }}
 * />
 *
 * @example
 * <InputMask mask="(999) 999-9999">
 *   <TextField label="Phone Number" hint="(555) 123-4567" />
 * </InputMask>
 *
 * @example
 * <InputMask
 *   mask="99/99/9999"
 *   customMaskHandler={[
 *     {
 *       match: {
 *         matchPattern: /^(\d)\//,
 *         startPosition: 0,
 *         endPosition: 2,
 *       },
 *       apply: {
 *         applyPattern: "0$1",
 *         caretOffset: 2,
 *       },
 *     },
 *   ]}
 *   inputProps={{ label: "Date with custom handler" }}
 * />
 */
export const InputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  (
    {
      children,
      mask,
      defaultMaskCharacter = "_",
      value,
      defaultValue,
      onChange,
      inputProps,
    },
    ref,
  ) => {
    const [v, setV] = useOptionallyControlledState({
      controlledValue:
        value !== undefined
          ? applyMask(value, mask, defaultMaskCharacter)
          : undefined,
      defaultValue: applyMask(defaultValue || "", mask, defaultMaskCharacter),
      // Omitting the onChange handler here because we want to handle it ourselves (i.e. send an event instead of just the value)
    });
    const specialChange = useRef<SpecialChange | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    const passInRef = useMergeRefs([inputRef, ref]);

    const currentOpenCharacterCount = useMemo(() => {
      return v.split("").filter((char) => char === defaultMaskCharacter).length;
    }, [v, defaultMaskCharacter]);

    const fixedCharacterIndices = useMemo(() => {
      const fixedIndices: number[] = [];
      mask.split("").forEach((char, i) => {
        if (!MASK_CHARS.includes(char)) {
          fixedIndices.push(i);
        }
      });
      return fixedIndices;
    }, [mask]);

    const moveCursorTo = (start: number, end: number = start) => {
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(start, end);
      });
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      let newVal = applyMask(e.target.value, mask, defaultMaskCharacter);
      if (
        // A single character was backspaced
        specialChange.current?.reason === "Backspace" &&
        specialChange.current.start === specialChange.current.end
      ) {
        // If the character before the backspace is a fixed character, we need to remove the filled character before the fixed character
        if (fixedCharacterIndices.includes(specialChange.current.start - 1)) {
          const removeIndex = getLastFilledCharacterIndex(
            newVal.slice(0, specialChange.current.start),
            mask,
          );
          newVal = applyMask(
            newVal.slice(0, removeIndex) + newVal.slice(removeIndex + 1),
            mask,
            defaultMaskCharacter,
          );
          moveCursorTo(removeIndex, removeIndex);
        } else {
          const idx = Math.min(
            getLastFilledCharacterIndex(newVal, mask) + 1,
            specialChange.current.start - 1,
          );
          moveCursorTo(idx, idx);
        }
      } else if (
        // A single character was deleted
        specialChange.current?.reason === "Delete" &&
        specialChange.current.start === specialChange.current.end
      ) {
        // If the character after the delete is a fixed character, we need to remove the filled character after the fixed character
        if (fixedCharacterIndices.includes(specialChange.current.start)) {
          const removeIndex = getFirstFilledCharacterIndex(
            newVal.slice(specialChange.current.start + 1),
            mask,
          );
          newVal = applyMask(
            newVal.slice(0, removeIndex) + newVal.slice(removeIndex + 1),
            mask,
            defaultMaskCharacter,
          );
        }
        const firstFilledIdx = getFirstFilledCharacterIndex(newVal, mask);
        const targetIdx = Math.max(firstFilledIdx, specialChange.current.start);
        moveCursorTo(targetIdx, targetIdx);
      } else if (
        specialChange.current?.reason === "Edit" &&
        specialChange.current.start === specialChange.current.end // A single character was edited
      ) {
        const firstNonFixedIdx = getFirstNonFixedCharacterIndex(
          specialChange.current.start + 1,
          fixedCharacterIndices,
        );
        moveCursorTo(firstNonFixedIdx, firstNonFixedIdx);
      } else {
        const newIndex = getNextOpenCharacterIndex(
          newVal,
          defaultMaskCharacter,
        );
        moveCursorTo(newIndex, newIndex);
      }

      setV(newVal);
      if (inputRef.current) {
        inputRef.current.value = newVal;
      }
      onChange?.({ ...e, target: { ...e.target, value: newVal } });
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      requestAnimationFrame(() => {
        if (
          e.target.selectionStart === null ||
          e.target.selectionStart === undefined ||
          e.target.selectionStart >= v.length
        ) {
          const newIndex = getNextOpenCharacterIndex(v, defaultMaskCharacter);
          moveCursorTo(newIndex, newIndex);
        }
      });
      inputProps?.onFocus?.(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (target.selectionStart === null || target.selectionEnd === null) {
        return;
      }
      if (e.key === "Backspace") {
        specialChange.current = {
          reason: "Backspace",
          start: target.selectionStart,
          end: target.selectionEnd,
        };
      } else if (e.key === "Delete") {
        specialChange.current = {
          reason: "Delete",
          start: target.selectionStart,
          end: target.selectionEnd,
        };
      } else if (currentOpenCharacterCount === 0) {
        specialChange.current = {
          reason: "Edit",
          start: target.selectionStart,
          end: target.selectionEnd,
        };
      } else {
        specialChange.current = null;
      }
      inputProps?.onKeyDown?.(e);
    };

    const handleClick = (e: MouseEvent<HTMLInputElement>) => {
      // For future use.
      e.preventDefault();
      e.stopPropagation();
      inputProps?.onClick?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      // For future use.
      e.preventDefault();
      e.stopPropagation();
      inputProps?.onBlur?.(e);
    };

    const mergedInputProps = {
      ...inputProps,
      ...(children && isValidElement(children) ? children.props : {}),
      value: v,
      onChange: handleChange,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      onBlur: handleBlur,
      ref: passInRef,
    };

    const isInputEmpty = useMemo(() => {
      if (v.length === 0) {
        return true;
      }
      return v === applyMask("", mask, defaultMaskCharacter);
    }, [v, defaultMaskCharacter, mask]);

    return children && isValidElement(children) && children.type === "input" ? (
      cloneElement(children, {
        ...mergedInputProps,
      } as InputHTMLAttributes<HTMLInputElement>)
    ) : (
      <TextField
        data-input-empty={isInputEmpty}
        {...(mergedInputProps as TextFieldProps)}
      />
    );
  },
);

InputMask.displayName = "InputMask";

/**
 * This function applies the mask to the input value, returning a new string that matches the mask's format.
 * @param value - a string representing the raw input value (e.g., what the user typed)
 * @param mask -  a string representing the desired input mask (e.g., "99/99/9999" for a date)
 * @param defaultMaskCharacter - a string representing the default mask character (e.g., "_")
 * @returns a string representing the masked value (e.g., "12/31/2025" for a date)
 *
 * @example
 * applyMask("1234567890", "99/99/9999", "_") // "12/31/2025"
 * applyMask("1234567890", "999-999-9999", "-") // "123-456-7890"
 * applyMask("123", "99/99", "_") // "12/3_"
 */
function applyMask(value: string, mask: string, defaultMaskCharacter: string) {
  let maskedValue = "";
  let valueIndex = 0;
  for (let i = 0; i < mask.length; i++) {
    const maskChar = mask[i];
    if (MASK_CHARS.includes(maskChar)) {
      // Find next acceptable char in value
      while (
        valueIndex < value.length &&
        !isAcceptableChar(value[valueIndex], maskChar)
      ) {
        valueIndex++;
      }
      if (valueIndex < value.length) {
        maskedValue += value[valueIndex];
        valueIndex++;
      } else {
        maskedValue += defaultMaskCharacter;
      }
    } else {
      maskedValue += maskChar;
    }
  }
  return maskedValue;
}

/**
 * This function checks if a character is acceptable for a given mask character.
 * @param char - a string representing the character to check
 * @param maskChar - a string representing the mask character
 * @returns a boolean indicating if the character is acceptable
 */
function isAcceptableChar(char: string, maskChar: string) {
  if (maskChar === "9") {
    return /\d/.test(char);
  }
  if (maskChar === "a") {
    return /[a-zA-Z]/.test(char);
  }
  if (maskChar === "*") {
    return /[a-zA-Z0-9]/.test(char);
  }
  return false;
}

/**
 * This function finds the next open character in a given value.
 * @param value - a string representing the value to search
 * @param defaultMaskCharacter - a string representing the default mask character (e.g., "_")
 * @returns a number representing the index of the next open character
 */
function getNextOpenCharacterIndex(
  value: string,
  defaultMaskCharacter: string,
) {
  let idx = value.indexOf(defaultMaskCharacter);
  if (idx < 0) {
    idx = value.length;
  }
  return idx;
}

/**
 * This function finds the first non-fixed character in the value.
 * @param startingIndex - a number representing the index to start searching from
 * @param fixedCharacterIndices - an array of numbers representing the indices of the fixed characters
 * @returns a number representing the index of the first non-fixed character
 */
function getFirstNonFixedCharacterIndex(
  startingIndex: number,
  fixedCharacterIndices: number[],
) {
  let idx = startingIndex;
  while (fixedCharacterIndices.includes(idx)) {
    idx++;
  }
  return idx;
}

/**
 * This function finds the last filled character in a given value.
 * @param value - a string representing the value to search
 * @param mask - a string representing the mask
 * @returns a number representing the index of the last filled character
 */
function getLastFilledCharacterIndex(value: string, mask: string) {
  let idx = value
    .split("")
    .findLastIndex((char, i) => isAcceptableChar(char, mask[i]));
  if (idx < 0) {
    idx = 0;
  }
  return idx;
}

/**
 * This function finds the first filled character in a given value.
 * @param value - a string representing the value to search
 * @param mask - a string representing the mask
 * @returns a number representing the index of the first filled character
 */
function getFirstFilledCharacterIndex(value: string, mask: string) {
  let idx = value
    .split("")
    .findIndex((char, i) => isAcceptableChar(char, mask[i]));
  if (idx < 0) {
    idx = 0;
  }
  return idx;
}
